import { useCallback, useEffect, useRef } from 'react';
import type { ClassifyPrediction } from '../types';
import CardCandidateItem from './CardCandidateItem';

interface CardCandidateListProps {
  candidates: ClassifyPrediction[];
  onSelect: (prediction: ClassifyPrediction) => void;
  onBack?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  total?: number;
}

export default function CardCandidateList({
  candidates,
  onSelect,
  onBack,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  total,
}: CardCandidateListProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    if (entries[0]?.isIntersecting && hasMore && !isLoadingMore) {
      onLoadMore?.();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element || !onLoadMore) return;

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '160px',
      threshold: 0,
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver, onLoadMore]);

  if (candidates.length === 0) {
    return (
      <section className="rounded-[24px] border border-border bg-white px-6 py-14 text-center shadow-panel">
        <h2 className="text-xl font-semibold text-primary">No card candidates available</h2>
        <p className="mt-2 text-sm text-secondary">Try another image, printed number, or marketplace name.</p>
        {onBack && <button onClick={onBack} className="mt-6 rounded-xl bg-ink px-5 py-2.5 text-sm font-semibold text-white">Reset search</button>}
      </section>
    );
  }

  const resultSummary = total !== undefined
    ? `${total.toLocaleString()} possible ${total === 1 ? 'identity' : 'identities'} · ${candidates.length.toLocaleString()} loaded`
    : `${candidates.length.toLocaleString()} image ${candidates.length === 1 ? 'match' : 'matches'}`;

  return (
    <section aria-labelledby="identity-candidates-heading">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-cyan">Identity candidates</p>
          <h2 id="identity-candidates-heading" className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-primary">Choose the exact printing</h2>
          <p className="mt-2 text-sm text-secondary">{resultSummary}. Check the artwork, printed number, and language before continuing.</p>
        </div>
        <div className="rounded-full border border-border bg-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-secondary-light">
          Prices open after confirmation
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {candidates.map((prediction, index) => (
          <CardCandidateItem
            key={`${prediction.id_label}-${prediction.card?.uid || index}`}
            prediction={prediction}
            rank={index}
            onSelect={onSelect}
          />
        ))}
      </div>

      <div ref={loadMoreRef} className="py-7" aria-live="polite">
        {isLoadingMore && (
          <div className="text-center">
            <div className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-border border-t-cyan" />
            <p className="mt-2 text-xs font-semibold text-secondary">Loading more identities…</p>
          </div>
        )}
        {!hasMore && total !== undefined && candidates.length >= total && (
          <p className="text-center text-xs font-semibold uppercase tracking-[0.1em] text-secondary-light">All matching identities loaded</p>
        )}
      </div>
    </section>
  );
}
